/**
 * useGroupedRequirements — pure selector.
 *
 * Given the project's flat list of requirements (from `useRequirements`),
 * produces a grouped, hierarchical view tailored to the current
 * `groupBy` mode and active filters in `requirementsViewStore`.
 *
 * Persona grouping computes the cascade CLIENT-SIDE: for each persona, we
 * find stories that link that persona directly, then include their ancestor
 * chain. Parents are multi-placed (appear under every persona whose
 * descendant stories link to them). Orphan stories go in "Unassigned".
 *
 * No network calls. Used by List + Notes.
 */

import { useMemo } from 'react'
import type { Requirement, RequirementSection, RequirementTheme } from '@ispot/shared'
import {
  REQUIREMENT_DOC_SECTIONS,
  REQUIREMENT_DOC_SECTION_LABELS,
  getRequirementSection,
} from '@ispot/shared'
import { useRequirementsViewStore, type RequirementsGroupBy } from '@/stores/requirementsViewStore'

export interface GroupedNode {
  requirement: Requirement
  /** Stable key for React. Includes the group scope so multi-placed parents don't collide. */
  key: string
  children: GroupedNode[]
}

export interface Group {
  /** Stable id for the group itself (e.g. persona:rachel-id, status:backlog). */
  id: string
  /** Display label in the group header. */
  label: string
  /** Optional sublabel (persona role, theme description, etc.) */
  sub?: string
  /** Dominant color for the header (hex or CSS var). */
  color?: string
  /** Initials / glyph for the avatar. */
  initials?: string
  /** Optional photo URL (persona avatar). Falls back to initials when missing/errored. */
  photo?: string
  /** Whether this is the "Unassigned" bucket (rendered muted + at bottom). */
  unassigned?: boolean
  /** Hierarchy tree of requirements in this group. */
  roots: GroupedNode[]
  /** Total descendant story count — useful for header stats. */
  storyCount: number
}

export interface GroupedView {
  groups: Group[]
  /** Flat list of requirements after filter (pre-grouping). Useful for counts. */
  filtered: Requirement[]
}

interface Ctx {
  requirements: Requirement[]
  themes: RequirementTheme[]
  /** Optional map: personaSessionId -> { name, role, color, initials, photo }. If empty,
   *  persona groups still render with id-only labels. */
  personaIndex?: Map<string, { name: string; role?: string; color?: string; initials?: string; photo?: string }>
}

/* ============================================================================
 *  PUBLIC HOOK
 * ========================================================================= */

export function useGroupedRequirements({ requirements, themes, personaIndex }: Ctx): GroupedView {
  const groupBy = useRequirementsViewStore(s => s.groupBy)
  const searchQuery = useRequirementsViewStore(s => s.searchQuery)
  const facetTypes = useRequirementsViewStore(s => s.facetTypes)
  const facetPriorities = useRequirementsViewStore(s => s.facetPriorities)
  const facetStatuses = useRequirementsViewStore(s => s.facetStatuses)
  const facetThemeIds = useRequirementsViewStore(s => s.facetThemeIds)

  return useMemo<GroupedView>(() => {
    const filtered = applyFilters(requirements, {
      searchQuery, facetTypes, facetPriorities, facetStatuses, facetThemeIds,
    })

    let groups: Group[]
    switch (groupBy) {
      case 'persona':
        groups = groupByPersona(filtered, personaIndex)
        break
      case 'theme':
        groups = groupByTheme(filtered, themes)
        break
      case 'status':
        groups = groupByScalar(filtered, 'status', STATUS_ORDER, STATUS_LABEL)
        break
      case 'priority':
        groups = groupByScalar(filtered, 'priority', PRIORITY_ORDER, PRIORITY_LABEL)
        break
      default:
        groups = groupByHierarchy(filtered)
    }

    return { groups, filtered }
  }, [requirements, themes, personaIndex, groupBy, searchQuery, facetTypes, facetPriorities, facetStatuses, facetThemeIds])
}

/* ============================================================================
 *  FILTERS
 * ========================================================================= */

function applyFilters(
  requirements: Requirement[],
  f: {
    searchQuery: string
    facetTypes: Set<string>
    facetPriorities: Set<string>
    facetStatuses: Set<string>
    facetThemeIds: Set<string>
  },
): Requirement[] {
  const q = f.searchQuery.trim().toLowerCase()
  return requirements.filter(r => {
    if (f.facetTypes.size && !f.facetTypes.has(r.type)) return false
    // Strict priority: when a priority filter is active, items with NO priority
    // are excluded (otherwise users see "filter by Critical" but null-priority
    // rows leak through and the filter feels broken).
    if (f.facetPriorities.size && (!r.priority || !f.facetPriorities.has(r.priority))) return false
    if (f.facetStatuses.size && !f.facetStatuses.has(r.status)) return false
    if (f.facetThemeIds.size) {
      const has = r.themeIds.some(id => f.facetThemeIds.has(id))
      if (!has) return false
    }
    if (q) {
      const hay = `${r.name} ${r.description ?? ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
}

/* ============================================================================
 *  HIERARCHY — all requirements under one synthetic group.
 * ========================================================================= */

function buildTree(requirements: Requirement[], scope?: string): GroupedNode[] {
  const byId = new Map(requirements.map(r => [r.id, r] as const))
  const children = new Map<string | null, Requirement[]>()
  for (const r of requirements) {
    const list = children.get(r.parentId) ?? []
    list.push(r)
    children.set(r.parentId, list)
  }
  for (const list of children.values()) {
    list.sort((a, b) => a.sortOrder - b.sortOrder)
  }
  function build(parentId: string | null): GroupedNode[] {
    const kids = children.get(parentId) ?? []
    return kids.map(r => ({
      requirement: r,
      key: scope ? `${scope}:${r.id}` : r.id,
      children: build(r.id),
    }))
  }
  // Roots = those whose parent isn't present (or is null).
  const roots = requirements.filter(r => !r.parentId || !byId.has(r.parentId))
  const rootIds = new Set(roots.map(r => r.id))
  return build(null)
    .filter(n => rootIds.has(n.requirement.id) || !n.requirement.parentId)
}

function countStories(nodes: GroupedNode[]): number {
  let n = 0
  for (const node of nodes) {
    if (node.requirement.type === 'story') n++
    n += countStories(node.children)
  }
  return n
}

/**
 * Hierarchy mode: split into three document sections (overview / functional /
 * non-functional). Epics live in the section recorded on `metadata.section`;
 * stories inherit from their parent epic. Empty sections are kept so authors
 * can drop a new epic into them.
 */
function groupByHierarchy(requirements: Requirement[]): Group[] {
  const byId = new Map<string, Requirement>()
  for (const r of requirements) byId.set(r.id, r)

  const bySection = new Map<RequirementSection, Requirement[]>()
  for (const s of REQUIREMENT_DOC_SECTIONS) bySection.set(s, [])
  for (const r of requirements) {
    const section = getRequirementSection(r, byId)
    bySection.get(section)!.push(r)
  }

  return REQUIREMENT_DOC_SECTIONS.map(sectionId => {
    const subset = bySection.get(sectionId)!
    return {
      id: `section:${sectionId}`,
      label: REQUIREMENT_DOC_SECTION_LABELS[sectionId],
      sub: sectionDescription(sectionId),
      initials: sectionInitial(sectionId),
      color: sectionColor(sectionId),
      roots: buildTree(subset, `s:${sectionId}`),
      storyCount: subset.filter(r => r.type === 'story').length,
    }
  })
}

function sectionDescription(s: RequirementSection): string {
  if (s === 'overview') return 'Product framing & scope'
  if (s === 'functional') return 'User-facing capabilities'
  return 'Quality attributes (NFR)'
}
function sectionInitial(s: RequirementSection): string {
  if (s === 'overview') return 'O'
  if (s === 'functional') return 'F'
  return 'N'
}
function sectionColor(s: RequirementSection): string {
  if (s === 'overview') return 'var(--color-warm-500)'
  if (s === 'functional') return 'var(--color-business)'
  return 'var(--color-warm-700)'
}

/* ============================================================================
 *  PERSONA — cascade client-side. Multi-placement; Unassigned bucket.
 * ========================================================================= */

function groupByPersona(
  requirements: Requirement[],
  personaIndex?: Map<string, { name: string; role?: string; color?: string; initials?: string; photo?: string }>,
): Group[] {
  const byId = new Map(requirements.map(r => [r.id, r] as const))

  // For each story, walk its ancestor chain and collect ids.
  function ancestorsOf(storyId: string): string[] {
    const out: string[] = []
    let cur = byId.get(storyId)
    while (cur?.parentId) {
      out.push(cur.parentId)
      cur = byId.get(cur.parentId)
    }
    return out
  }

  // Map personaId -> Set<requirementId that must appear>
  const persToReqIds = new Map<string, Set<string>>()
  const orphanStoryIds = new Set<string>()

  for (const r of requirements) {
    if (r.type !== 'story') continue
    if (r.personas.length === 0) {
      orphanStoryIds.add(r.id)
      continue
    }
    const chain = [r.id, ...ancestorsOf(r.id)]
    for (const p of r.personas) {
      const bucket = persToReqIds.get(p.personaSessionId) ?? new Set()
      for (const id of chain) bucket.add(id)
      persToReqIds.set(p.personaSessionId, bucket)
    }
  }

  const groups: Group[] = []
  // Stable order: by persona name (alpha) if index available, else by id.
  const ordered = Array.from(persToReqIds.keys()).sort((a, b) => {
    const na = personaIndex?.get(a)?.name ?? a
    const nb = personaIndex?.get(b)?.name ?? b
    return na.localeCompare(nb)
  })

  for (const pid of ordered) {
    const reqIdsInScope = persToReqIds.get(pid)!
    const subset = requirements.filter(r => reqIdsInScope.has(r.id))
    const meta = personaIndex?.get(pid)
    const scope = `p:${pid}`
    groups.push({
      id: scope,
      label: meta?.name ?? `Persona ${pid.slice(0, 6)}`,
      sub: meta?.role,
      color: meta?.color ?? 'var(--color-market)',
      initials: meta?.initials ?? (meta?.name?.slice(0, 2).toUpperCase() ?? '??'),
      photo: meta?.photo,
      roots: buildTree(subset, scope),
      storyCount: subset.filter(r => r.type === 'story').length,
    })
  }

  if (orphanStoryIds.size > 0) {
    const orphanSubset = requirements.filter(r => orphanStoryIds.has(r.id))
    groups.push({
      id: 'unassigned',
      label: 'Unassigned',
      sub: 'Stories without a primary persona',
      initials: '—',
      unassigned: true,
      roots: orphanSubset.map(r => ({ requirement: r, key: `unassigned:${r.id}`, children: [] })),
      storyCount: orphanSubset.length,
    })
  }

  return groups
}

/* ============================================================================
 *  THEME — one group per theme; requirements linked to a theme go under it.
 *  Stories without themes go in an "Untagged" bucket.
 * ========================================================================= */

function groupByTheme(requirements: Requirement[], themes: RequirementTheme[]): Group[] {
  const groups: Group[] = []
  for (const t of themes) {
    const subset = requirements.filter(r => r.themeIds.includes(t.id))
    if (subset.length === 0) continue
    const scope = `t:${t.id}`
    groups.push({
      id: scope,
      label: `#${t.name}`,
      color: t.color ?? undefined,
      initials: '#',
      roots: subset.map(r => ({ requirement: r, key: `${scope}:${r.id}`, children: [] })),
      storyCount: subset.filter(r => r.type === 'story').length,
    })
  }
  const untagged = requirements.filter(r => r.themeIds.length === 0 && r.type === 'story')
  if (untagged.length > 0) {
    groups.push({
      id: 'untagged',
      label: 'Untagged',
      initials: '—',
      unassigned: true,
      roots: untagged.map(r => ({ requirement: r, key: `untagged:${r.id}`, children: [] })),
      storyCount: untagged.length,
    })
  }
  return groups
}

/* ============================================================================
 *  STATUS + PRIORITY — flat by scalar field.
 * ========================================================================= */

const STATUS_ORDER = ['in-progress', 'in-review', 'planned', 'backlog', 'done']
const STATUS_LABEL: Record<string, string> = {
  'backlog': 'Backlog', 'planned': 'Planned',
  'in-progress': 'In progress', 'in-review': 'In review', 'done': 'Done',
}
const PRIORITY_ORDER = ['critical', 'high', 'medium', 'low']
const PRIORITY_LABEL: Record<string, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low',
}

function groupByScalar(
  requirements: Requirement[],
  field: 'status' | 'priority',
  order: string[],
  labels: Record<string, string>,
): Group[] {
  const groups: Group[] = []
  for (const value of order) {
    const subset = requirements.filter(r => r[field] === value)
    if (subset.length === 0) continue
    const scope = `${field}:${value}`
    groups.push({
      id: scope,
      label: labels[value] ?? value,
      sub: field === 'status' ? 'Status' : 'Priority',
      initials: field === 'status' ? '•' : '!',
      roots: subset.map(r => ({ requirement: r, key: `${scope}:${r.id}`, children: [] })),
      storyCount: subset.filter(r => r.type === 'story').length,
    })
  }
  return groups
}

// Re-export the type used by callers that pass `groupBy` around.
export type { RequirementsGroupBy }
